import React from "react"
import { Button } from "reactstrap"

// Global size configuration - change this to change size everywhere
const GLOBAL_BUTTON_SIZE = "md" // sm, md, lg

const CButton = ({
  icon,
  text,
  color = "primary",
  size = GLOBAL_BUTTON_SIZE,
  variant = "default", // default, outline, soft
  disabled = false,
  loading = false,
  className = "",
  iconPosition = "left", // left, right
  spacing = "me-1", // spacing between icon and text
  onClick,
  style = {},
  ...buttonProps
}) => {
  // Build icon element with consistent spacing
  const iconElement = icon && (
    <i
      className={`${icon}${
        text ? ` ${iconPosition === "right" ? "ms-1" : spacing}` : ""
      }`}
    ></i>
  )

  // Handle loading state
  const loadingElement = loading && (
    <span
      className={`spinner-border spinner-border-sm${text ? ` ${spacing}` : ""}`}
      role="status"
      aria-hidden="true"
    ></span>
  )

  // Determine button color based on variant
  const getButtonColor = () => {
    switch (variant) {
      case "outline":
        return `outline-${color}`
      case "soft":
        return `soft-${color}`
      default:
        return color
    }
  }

  // Build className
  const buildClassName = () => {
    let classes = []

    // Add base classes
    if (className) classes.push(className)

    // Add wave effects for interactive buttons
    if (!disabled && !loading) {
      classes.push("waves-effect waves-light")
    }

    return classes.join(" ")
  }

  // Render content based on iconPosition and text
  const renderContent = () => {
    if (loading) {
      return (
        <>
          {loadingElement}
          {text && (
            <span>
              {text.replace(/^(Create|Update|Add|Edit)/, "Processing")}
            </span>
          )}
        </>
      )
    }

    // If no icon, just return text
    if (!icon) {
      return text
    }

    if (iconPosition === "right") {
      return (
        <>
          <span>{text}</span>
          {iconElement}
        </>
      )
    }

    // Default: left position
    return (
      <>
        {iconElement}
        <span>{text}</span>
      </>
    )
  }

  return (
    <Button
      color={getButtonColor()}
      size={size}
      disabled={disabled || loading}
      className={buildClassName()}
      onClick={onClick}
      style={style}
      {...buttonProps}
    >
      {renderContent()}
    </Button>
  )
}

// Predefined color button variants
const AddButton = props => <CButton color="success" {...props} />
AddButton.displayName = "CButton.Add"

const PrimaryButton = props => <CButton color="primary" {...props} />
PrimaryButton.displayName = "CButton.Primary"

const SecondaryButton = props => <CButton color="secondary" {...props} />
SecondaryButton.displayName = "CButton.Secondary"

const DangerButton = props => <CButton color="danger" {...props} />
DangerButton.displayName = "CButton.Danger"

const WarningButton = props => <CButton color="warning" {...props} />
WarningButton.displayName = "CButton.Warning"

const InfoButton = props => <CButton color="info" {...props} />
InfoButton.displayName = "CButton.Info"

const LightButton = props => <CButton color="light" {...props} />
LightButton.displayName = "CButton.Light"

const DarkButton = props => <CButton color="dark" {...props} />
DarkButton.displayName = "CButton.Dark"

const SuccessButton = props => <CButton color="success" {...props} />
SuccessButton.displayName = "CButton.Success"

// Attach color variants to Button
CButton.Add = AddButton
CButton.Primary = PrimaryButton
CButton.Secondary = SecondaryButton
CButton.Danger = DangerButton
CButton.Warning = WarningButton
CButton.Info = InfoButton
CButton.Light = LightButton
CButton.Dark = DarkButton
CButton.Success = SuccessButton

export default CButton
export { GLOBAL_BUTTON_SIZE }
